import { Flex, Link } from '@adobe/react-spectrum';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { ErrorBoundary } from '@exchange-frontends/components';

import { useStore } from '../../../store';
import { useReviews } from '../../../utils/hooks';
import { UserDataContext } from '../../App';
import RatingsSection from './RatingsSection';
import { Review } from './Review';

export const Label = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.3;
  color: rgb(110, 110, 110);
  text-align: ${({ $centered }) => ($centered ? 'center' : 'left')};
`;
export const RedText = styled.span`
  color: red;
`;

const PAGE_SIZE = 5;

const ReviewsPage = ({ offset }) => {
  const { id: assetId } = useParams();
  const { accessToken, profile } = useContext(UserDataContext) ?? {};
  const { reviews = Array(5).fill(null), isLoading, error } = useReviews(assetId, offset, accessToken, profile?.userId);
  const myReviewId = useStore((state) => state.currentUserReview?.id);
  const isMyReviewLoading = useStore((state) => state.isMyReviewLoading);
  // This is to handle the case where the user has a review in the list
  // And deletes it from my review section
  // This could be avoided if the RnR API would follow the guidelines that were described during the workshops
  // and initially outlined in the wiki
  // But as with all things, there's one version in the wiki, another version in the swagger and yet another version implemented
  const [myReviewsArray, setMyReviewsArray] = useState([]);

  useEffect(() => {
    if (!myReviewsArray.includes(myReviewId)) {
      setMyReviewsArray((prev) => [...prev, myReviewId]);
    }
  }, [myReviewId, myReviewsArray]);

  const shouldShowReview = (reviewId) => myReviewId !== reviewId && !myReviewsArray.includes(reviewId);

  return (
    <ErrorBoundary
      isError={!!error}
      errorMessage={error?.userMessage ?? error?.message ?? 'Something went wrong while loading review.'}
    >
      <>
        {reviews?.map(
          (review, index) =>
            shouldShowReview(review?.id) && (
              <Review key={review?.id ?? index} review={review} isLoading={isLoading || isMyReviewLoading} />
            )
        )}
      </>
    </ErrorBoundary>
  );
};

export const RatingsAndReviews = () => {
  const totalReviews = useStore((state) => state.totalReviews);
  const shouldShowRnr = useStore((state) => state.shouldShowRnr);

  const [offset, setOffset] = useState(0);
  const [pageOffsets, setPageOffsets] = useState([]);

  const loadedReviews = offset + PAGE_SIZE;
  const shouldShowLoadMore = loadedReviews < totalReviews;

  useEffect(() => {
    setPageOffsets((state) => [...state, offset]);
  }, [offset]);

  return (
    <Flex direction="column" gap="size-400">
      <RatingsSection shouldShowRnr={shouldShowRnr} />
      {shouldShowRnr && (
        <>
          {pageOffsets.map((offset) => (
            <ReviewsPage key={offset} offset={offset} />
          ))}
          {shouldShowLoadMore && <Link onPress={() => setOffset((state) => state + PAGE_SIZE)}>Load more reviews</Link>}
        </>
      )}
    </Flex>
  );
};
